import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Upload,
  Button,
  Row,
  Col,
  message,
  Flex,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  EnhancementCase,
  EnhancementField,
} from "../../../interfaces/IEnhancement";
import { IDocumentFields } from "../../../interfaces/ICase";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import FormItem from "antd/es/form/FormItem";
import {
  fetchProceduresForEnhancement,
  fetchSpecialitiesForEnhancement,
} from "../../../models/EnhancementSlice";
import { IFetchAllDetails } from "../../../interfaces/IPatient";
import { errorMessageModal } from "../../../utils/modalMessage";

interface NewEnhancementModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  fields: EnhancementCase;
  onFieldChange: any;
  handleFileChange?: any;
  initialData: EnhancementCase | null;
  uploadedDocumentUrl: Array<IDocumentFields>;
  handleDeleteList: CallableFunction;
  handleSetDocumentUrl: CallableFunction;
  viewFile: CallableFunction;
  handleOpen: any;
  handleDeficiencyModal?: any;
  closeDeficiencyModal?: any;
  deficiencyModal: boolean;
  deficiencyQuestions?: any;
  previewDocumentUrl: string;
  setDocumentName: any;
  handleDeficiencyModalAfterSave: any;
  patientDetails?: IFetchAllDetails | null;
  handleDownloadDocument?: any;
  isDownloadingDocument?: boolean;
  downloadingDocumentUrl?: string;
}

const NewEnhancementModal: React.FC<NewEnhancementModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  fields,
  onFieldChange,
  handleFileChange,
  initialData,
  uploadedDocumentUrl,
  handleDeleteList,
  handleSetDocumentUrl,
  viewFile,
  handleOpen,
  handleDeficiencyModal,
  closeDeficiencyModal,
  deficiencyModal,
  deficiencyQuestions,
  previewDocumentUrl,
  setDocumentName,
  handleDeficiencyModalAfterSave,
  patientDetails,
  handleDownloadDocument,
  isDownloadingDocument,
  downloadingDocumentUrl,
}) => {
  const [form] = Form.useForm();
  const { hospital, visit, patients } = useSelector(
    (state: RootState) => state
  );
  const [fileList, setFileList] = useState<any[]>([]);
  const { Text } = Typography;
  const { isSaving } = useSelector((state: RootState) => state.enhancement);
  const [documentShowRendered, setDocumentShowRendered] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.healthCareId &&
      (patientDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "CGHS" ||
        patientDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
          "CAPF")
    ) {
      dispatch(
        fetchProceduresForEnhancement({
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          isCghs:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
            "CGHS"
              ? true
              : false,
          procedureType:
            patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
            "OP"
              ? "OP"
              : "IP",
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        })
      );
      dispatch(fetchSpecialitiesForEnhancement());
    }
  }, [dispatch, patients?.fetchBasicDetails?.casesResponse?.healthCareId, patientDetails]);

  useEffect(() => {
    if (initialData) {
      const formValues: any = {};
      Object.entries(initialData)?.forEach(([sectionKey, sectionData]) => {
        sectionData?.forEach((field: EnhancementField) => {
          if (field.data !== null) {
            if (
              field.id === "dateOfAdmission" ||
              field.id === "dateOfDischarge"
            ) {
              formValues[field.id] = dayjs(field.data, "DD-MM-YYYY HH:mm:ss");
            } else if (field.id !== "remarks") {
              // Exclude remarks from initial data
              formValues[field.id] = field.data;
            }
          }
        });
      });
      form.setFieldsValue(formValues);
    }
  }, [initialData, form]);

  const calculateTotal = (values: any) => {
    const fieldsToSum = [
      "packages",
      "roomRentAndNursingCharge",
      "consultation",
      "otCharge",
      "surgeonAndAnesthetist",
      "investigation",
      "medicineAndConsumables",
      "otherHospitalExpenses",
    ];

    return fieldsToSum.reduce((total, fieldId) => {
      const value = values[fieldId];
      return total + (value ? parseFloat(value) || 0 : 0);
    }, 0);
  };

  const renderField = (field: EnhancementField) => {
    if (field.type === "text-show" || field.type === "input") {
      if (
        field.id === "totalAmount" ||
        field.id === "dateOfAdmission" ||
        field.id === "dateOfDischarge"
      ) {
        if (field.id === "dateOfAdmission") {
          return (
            <DatePicker
              disabled
              format="DD-MM-YYYY HH:mm:ss"
              value={field.data ? field.data : ""}
            />
          );
        }
        if (field.id === "dateOfDischarge") {
          return (
            <DatePicker
              showTime
              format="DD-MM-YYYY HH:mm:ss"
              onChange={(date, dateString) =>
                onFieldChange && onFieldChange(1, field.id, dateString)
              }
            />
          );
        }
        return <Input disabled />;
      }
      // if(field.id === "packages"){
      //   return (
      //   <Input
      //     type="number"
      //     id={field.id}
      //     placeholder="Enter Your Response here.."
      //     onChange={(e) => {
      //       onFieldChange && onFieldChange(1, field.id, e.target.value);

      //     }}
      //   />
      //   )
      // }

      return (
        <Input
          type="text"
          id={field.id}
          placeholder="Enter Your Response here..."
          onChange={(e) => {
            onFieldChange && onFieldChange(1, field.id, e.target.value);
            if (field.id !== "totalAmount") {
              const currentValues = form.getFieldsValue();
              currentValues[field.id] = e.target.value;
              const newTotal = calculateTotal(currentValues);
              form.setFieldsValue({ totalAmount: newTotal.toFixed(2) });
              onFieldChange &&
                onFieldChange(1, "totalAmount", newTotal.toFixed(2));
            }
          }}
        />
      );
    }
    const allowedFileTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/bmp",
      "image/webp",
      "application/pdf",
    ];

    const maxFileSize = 15 * 1024 * 1024;

    const beforeUpload = (file: any) => {
      if (!allowedFileTypes.includes(file.type)) {
        errorMessageModal(
          "You can only upload image files (JPEG, PNG, GIF, BMP, WebP) or PDF files!"
        );
        return Upload.LIST_IGNORE;
      }
      if (file.size > maxFileSize) {
        errorMessageModal("File must be smaller than 15 MB!");
        return Upload.LIST_IGNORE;
      }
      return false;
    };

    switch (field.type) {
      case "text-show":
      case "input":
        if (field.id === "dateOfAdmission") {
          return (
            <DatePicker
              disabled
              format="DD-MM-YYYY HH:mm:ss"
              value={field.data ? field.data : ""}
            />
          );
        }
        if (field.id === "dateOfDischarge") {
          return (
            <DatePicker
              showTime
              format="DD-MM-YYYY HH:mm:ss"
              onChange={(date, dateString) =>
                onFieldChange && onFieldChange(1, field.id, dateString)
              }
            />
          );
        }
        return (
          <Input
            type="text"
            id={field.id}
            placeholder="Enter Your Response here..."
            onChange={(e) =>
              onFieldChange && onFieldChange(1, field.id, e.target.value)
            }
          />
        );
      case "document-upload":
      case "document-show":
        return (
          <Row style={{ width: "100%" }}>
            <Upload
              id={field.id}
              accept="image/*,.pdf"
              beforeUpload={beforeUpload}
              showUploadList={false}
              // fileList={fileList}
              onChange={
                handleFileChange &&
                handleFileChange(field?.labelId, field?.label)
              }
              // multiple={true}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
              <div
                className="file-type-info"
                style={{ marginTop: "10px", textAlign: "center" }}
              >
                <Text type="secondary">
                  <FileImageOutlined /> Images (JPEG, PNG, GIF, BMP, WebP) or
                  <FilePdfOutlined /> PDF only &nbsp;
                </Text>
                <Text type="secondary">max 15 MB each</Text>
              </div>
            </Upload>
            <Col
              xs={24}
              md={24}
              sm={24}
              lg={24}
              xl={24}
              style={{ marginTop: "15px" }}
            >
              {uploadedDocumentUrl &&
                uploadedDocumentUrl?.length > 0 &&
                uploadedDocumentUrl
                  ?.filter((doc) => doc?.documentLabelType === field?.label)
                  ?.map((document, i) => (
                    <div className="file-list-container" key={i}>
                      <ul
                        className="list-unstyled"
                        style={{ marginTop: "0rem" }}
                      >
                        <li className="file-item" style={{ display: "flex" }}>
                          <span className="file-name" style={{ width: "30%" }}>
                            {document?.documentName
                              ? document?.documentName.length > 10
                                ? document?.documentName?.substring(0, 10) +
                                  "..."
                                : document?.documentName
                              : ""}
                          </span>

                          <Flex
                            // justify="flex-end"
                            align="center"
                            gap="small"
                            className="fa-icons"
                            style={{ width: "20%" }}
                          >
                            {isDownloadingDocument &&
                            downloadingDocumentUrl === document?.documentUrl ? (
                              <LoadingOutlined
                                style={{ color: "#273881", fontSize: "18px" }}
                              />
                            ) : (
                              <DownloadOutlined
                                style={{
                                  color: "#273881",
                                  fontSize: "18px",
                                }}
                                onClick={() =>
                                  handleDownloadDocument &&
                                  handleDownloadDocument(
                                    document?.documentName,
                                    document?.documentUrl
                                  )
                                }
                              />
                            )}
                            <EyeOutlined
                              style={{
                                color: "#273881",
                                fontSize: "18px",
                              }}
                              onClick={() => {
                                handleOpen && handleOpen();
                                handleSetDocumentUrl &&
                                  handleSetDocumentUrl(document?.documentUrl);
                              }}
                            />
                            <DeleteOutlined
                              style={{
                                color: "#273881",
                                fontSize: "18px",
                              }}
                              onClick={() =>
                                handleDeleteList &&
                                handleDeleteList(
                                  document?.documentLabelType,
                                  document?.documentUrl,
                                  i
                                )
                              }
                            />
                            {document?.deficiencyCheckStatus ? (
                              <Button
                                onClick={() => {
                                  if (document && document?.documentName) {
                                    setDocumentName(document?.documentName);
                                  }
                                  if (document?.documentType) {
                                    handleDeficiencyModal(
                                      document?.documentUrl,
                                      parseInt(document?.documentType),
                                      document
                                    );
                                  }
                                }}
                                block
                                type="primary"
                                style={{ backgroundColor: "#198754" }}
                              >
                                DEFICIENCY PASSED
                              </Button>
                            ) : document?.deficiencyCheckStatus === false ? (
                              <Button
                                onClick={() => {
                                  if (document && document?.documentName) {
                                    setDocumentName(document?.documentName);
                                  }
                                  if (document?.documentType) {
                                    handleDeficiencyModal(
                                      document?.documentUrl,
                                      parseInt(document?.documentType),
                                      document
                                    );
                                  }
                                }}
                                block
                                type="primary"
                                danger
                              >
                                DEFICIENCY FAILED
                              </Button>
                            ) : (
                              (Array?.isArray(deficiencyQuestions) ||
                                typeof deficiencyQuestions === "object") && (
                                <Button
                                  onClick={() => {
                                    if (document && document?.documentName) {
                                      setDocumentName(document?.documentName);
                                    }
                                    if (document?.documentType) {
                                      handleDeficiencyModal(
                                        document?.documentUrl,
                                        parseInt(document?.documentType),
                                        document
                                      );
                                    }
                                  }}
                                  block
                                  type="primary"
                                  htmlType="submit"
                                  style={{ fontSize: "7px" }}
                                >
                                  DEFICIENCY CHECK
                                </Button>
                              )
                            )}
                          </Flex>
                        </li>
                      </ul>
                    </div>
                  ))}
            </Col>
          </Row>
        );
      default:
        return <Input />;
    }
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const enhancedValues = {
          ...values,
          dateOfDischarge: values.dateOfDischarge
            ? dayjs(values.dateOfDischarge).format("DD-MM-YYYY HH:mm:ss")
            : null,
          files: fileList?.map((file) => ({
            file: file.originFileObj,
            documentType: file.name,
          })),
          totalAmount: calculateTotal(values).toFixed(2),
        };
        onSubmit(enhancedValues);
        setFileList([]);
        form.setFieldsValue({ remarks: "" });
      })
      .catch((info) => {
        errorMessageModal("Please fill in all required fields");
      });
  };

  return (
    <Modal
      visible={visible}
      title="New Enhancement Case"
      onCancel={() => {
        onCancel();
        setFileList([]);
        setDocumentShowRendered(false);
      }}
      onOk={handleSubmit}
      width={800}
      okButtonProps={{ loading: isSaving, disabled: isSaving }}
      cancelButtonProps={{ disabled: isSaving }}
      okText={isSaving ? "Saving..." : "Save"}
      style={{ top: 20 }}
    >
      <Form form={form} layout="vertical">
        {Object.entries(fields)
          .reverse()
          ?.map(([sectionKey, sectionData]) => (
            <React.Fragment key={sectionKey}>
              <h3>{sectionKey}</h3>
              <Row gutter={16}>
                {sectionData?.map((field) => (
                  <Col span={12} key={field.id}>
                    <Form.Item
                      label={field.label}
                      name={field.id}
                      rules={[
                        {
                          required: field.required,
                          message: `Please input ${field.label}`,
                        },
                        ...([
                          "packages",
                          "roomRentAndNursingCharge",
                          "consultation",
                          "otCharge",
                          "surgeonAndAnesthetist",
                          "investigation",
                          "medicineAndConsumables",
                          "otherHospitalExpenses",
                          "patientPaidAmount",
                        ].includes(field?.id)
                          ? [
                              {
                                pattern: /^\d+$/,
                                message: `${field?.label} must be a number`,
                              },
                            ]
                          : []),
                      ]}
                    >
                      {renderField(field)}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </React.Fragment>
          ))}
      </Form>
    </Modal>
  );
};

export default NewEnhancementModal;
